export type Sealed = { version: 1; iv: string; data: string };

const encoder = new TextEncoder();

export function base64(bytes: Uint8Array): string {
  let output = '';

  for (let i = 0; i < bytes.length; i += 8192) {
    output += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }

  return btoa(output);
}

export function unbase64(text: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(text), c => c.charCodeAt(0));
}

export async function importKey(raw: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', unbase64(raw), 'AES-GCM', false, ['encrypt', 'decrypt']);
}

export function randomKey(): string {
  return base64(crypto.getRandomValues(new Uint8Array(32)));
}

export async function seal(value: unknown, key: CryptoKey, context: string): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const data = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: encoder.encode(context) },
    key,
    encoder.encode(JSON.stringify(value)),
  );

  return { version: 1, iv: base64(iv), data: base64(new Uint8Array(data)) };
}

export async function unseal<T>(sealed: Sealed, key: CryptoKey, context: string): Promise<T> {
  if (sealed.version !== 1) {
    throw new Error('Unsupported encrypted format');
  }

  const raw = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: unbase64(sealed.iv), additionalData: encoder.encode(context) },
    key,
    unbase64(sealed.data),
  );

  return JSON.parse(new TextDecoder().decode(raw)) as T;
}

export type PasswordPolicy = {
  uppercase: boolean;
  lowercase: boolean;
  digits: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
};

export const defaultPasswordPolicy: PasswordPolicy = {
  uppercase: true,
  lowercase: true,
  digits: true,
  symbols: true,
  excludeAmbiguous: true,
};

export function generatePassword(
  length = 24,
  policy: PasswordPolicy = defaultPasswordPolicy,
): string {
  if (!Number.isInteger(length) || length < 16 || length > 128) {
    throw new Error('Use 16–128 characters');
  }

  const groups = [
    policy.uppercase ? 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' : '',
    policy.lowercase ? 'abcdefghijklmnopqrstuvwxyz' : '',
    policy.digits ? '0123456789' : '',
    policy.symbols ? '!@#$%&*+-=?' : '',
  ]
    .filter(Boolean)
    .map(group => (policy.excludeAmbiguous ? group.replace(/[IlO01o]/g, '') : group));

  if (!groups.length) {
    throw new Error('Select at least one character group');
  }

  const alphabet = groups.join(''),
    ceiling = 256 - (256 % alphabet.length);

  // Rejection sampling avoids modulo bias; rejecting whole candidates keeps a
  // uniform distribution over passwords satisfying every selected group.
  for (;;) {
    let password = '';

    while (password.length < length) {
      for (const value of crypto.getRandomValues(new Uint8Array(128))) {
        if (value < ceiling) {
          password += alphabet[value % alphabet.length];
        }

        if (password.length === length) {
          break;
        }
      }
    }

    if (groups.every(group => [...password].some(char => group.includes(char)))) {
      return password;
    }
  }
}
